import { resolvePrecision, formatToPrecision, formatWithGrouping } from '@/util/formatting'
import {
  WEIGHT_OZT_MAX,
  WEIGHT_OZT_MIN,
  PERCENT_MAX,
  PERCENT_MIN,
  PURITY_DECIMAL_MAX,
  PURITY_DECIMAL_MIN,
  FIRE_ASSAY_GRAMS_MIN,
  FIRE_ASSAY_GRAMS_MAX,
} from '@/util/refinery/constants'

/**
 * Get decimal precision for the precision directive
 * Supports semantic strings ('weight', 'currency') and explicit numbers
 * 
 * @param {Object} binding - Vue directive binding
 * @returns {number} Decimal places to apply
 */
function getDecimals(binding) {
  const value = binding.value
  
  // Use centralized resolver for semantic strings or explicit numbers
  return resolvePrecision(value)
}

/**
 * Normalize user input to handle edge cases before parsing
 * Converts leading/trailing decimals to parseable format
 * Strips thousands separators (commas) that may have been added by formatting
 * 
 * @param {string} rawValue - Raw input value from user
 * @returns {string} Normalized value ready for parseFloat
 */
function normalizeInputValue(rawValue) {
  // Strip thousands separators (commas) that may have been added by NumberField or other formatters
  // e.g., "1,000" → "1000", "1,234.56" → "1234.56"
  let normalized = rawValue.replace(/,/g, '')
  
  // Handle leading decimal: ".5" → "0.5"
  if (normalized.startsWith('.')) {
    normalized = '0' + normalized
  }
  
  // Handle trailing decimal: "5." → "5.0"
  if (normalized.endsWith('.') && normalized.length > 1) {
    normalized = normalized + '0'
  }
  
  return normalized
}

function formatInput(input, decimals, options = {}) {
  const { grouping = false } = options
  const normalizedValue = normalizeInputValue(input.value)
  const value = parseFloat(normalizedValue)
  
  if (isNaN(value) || input.value === '') return
  const formatted = grouping ? formatWithGrouping(value, decimals) : formatToPrecision(value, decimals)
  
  if (input.value !== formatted) {
    input.value = formatted
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }
}

/**
 * Get min/max clamp pair for a semantic precision type
 * Returns null when no clamp should be applied
 */
function getClampRange(binding) {
  // Allow specifying clamp type via directive arg, e.g. v-precision:percent="4"
  // Falls back to checking binding.value when a semantic string is passed directly
  const v = binding.value
  const type = binding.arg || (typeof v === 'string' ? v : null)
  switch (type) {
    case 'weight':
      return { min: WEIGHT_OZT_MIN, max: WEIGHT_OZT_MAX }
    case 'percent':
      return { min: PERCENT_MIN, max: PERCENT_MAX }
    case 'purity':
      return { min: PURITY_DECIMAL_MIN, max: PURITY_DECIMAL_MAX }
    case 'grams-fire-gross':
      return { min: FIRE_ASSAY_GRAMS_MIN, max: FIRE_ASSAY_GRAMS_MAX }
    case 'grams-fire-net':
      // no clamping on input for net weight; formatting still applies
      return null
    default:
      return null
  }
}

function isCurrency(binding) {
  const v = binding.value
  const arg = binding.arg
  return arg === 'currency' || v === 'currency' || arg === 'currency-precise' || v === 'currency-precise'
}

function shouldFormatLive(binding) {
  return !!(binding && binding.modifiers && binding.modifiers.live)
}

function shouldGroup(binding, input) {
  // Enable grouping if:
  // 1. `.group` modifier is explicitly used, OR
  // 2. It's a currency input (existing behavior for backward compatibility)
  const hasGroupModifier = !!(binding && binding.modifiers && binding.modifiers.group)
  const isCurrencyInput = isCurrency(binding) && input.type !== 'number'
  return hasGroupModifier || isCurrencyInput
}

function clampOnInput(input, binding) {
  const range = getClampRange(binding)
  if (!range) return
  const raw = input.value
  if (raw === '' || raw === '-' || raw === '.') return
  const normalized = normalizeInputValue(raw)
  const num = parseFloat(normalized)
  if (isNaN(num)) return

  // Always cap upper bound immediately
  if (range.max !== undefined && num > range.max) {
    input.value = String(range.max)
    input.dispatchEvent(new Event('input', { bubbles: true }))
    return
  }

  // Disallow negatives right away; allow 0 while typing and defer min to blur
  if (num < 0) {
    input.value = '0'
    input.dispatchEvent(new Event('input', { bubbles: true }))
    return
  }
}

function clampOnBlur(input, binding) {
  const range = getClampRange(binding)
  if (!range) return
  const raw = input.value
  if (raw === '') return
  const normalized = normalizeInputValue(raw)
  let num = parseFloat(normalized)
  if (isNaN(num)) return
  // Do not auto-clamp minimum for fire assay gross grams; show validation elsewhere
  const type = binding.arg || (typeof binding.value === 'string' ? binding.value : null)
  if (type === 'grams-fire-gross') {
    return
  }
  if (range.min !== undefined && num > 0 && num < range.min) {
    num = range.min
    input.value = String(num)
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }
}

export default {
  bind(el, binding) {
    // Handle both direct input elements and wrapper elements containing inputs
    const input = el.tagName === 'INPUT' ? el : el.querySelector('input')
    if (!input || (input.type !== 'number' && input.type !== 'text')) return
    
    const blurHandler = () => {
      clampOnBlur(input, binding)
      const decimals = getDecimals(binding)
      formatInput(input, decimals, { grouping: shouldGroup(binding, input) })
    }
    const inputHandler = () => {
      clampOnInput(input, binding)
      // Opt-in: when using `.live` modifier with currency, format as user types
      // But avoid doing this on type=number inputs because it disrupts typing
      if (shouldFormatLive(binding) && isCurrency(binding) && input.type !== 'number') {
        if (el._precisionFormatting) {
          // Prevent recursive input events when we dispatch after formatting
          el._precisionFormatting = false
          return
        }
        el._precisionFormatting = true
        const decimals = getDecimals(binding)
        // This will dispatch an input event; the guard above prevents infinite loops
        formatInput(input, decimals, { grouping: shouldGroup(binding, input) })
      }
    }
    
    input.addEventListener('blur', blurHandler)
    input.addEventListener('input', inputHandler)
    el._precisionBlurHandler = blurHandler
    el._precisionInputHandler = inputHandler
    el._precisionLastDecimals = getDecimals(binding)
  },
  
  // Ensure initial render shows fixed decimals (e.g., when input is disabled)
  inserted(el, binding) {
    // Handle both direct input elements and wrapper elements containing inputs
    const input = el.tagName === 'INPUT' ? el : el.querySelector('input')
    if (!input || (input.type !== 'number' && input.type !== 'text')) return
    if (input.value !== '') {
      const decimals = getDecimals(binding)
      formatInput(input, decimals, { grouping: shouldGroup(binding, input) })
    }
  },
  
  update(el, binding) {
    // Handle both direct input elements and wrapper elements containing inputs
    const input = el.tagName === 'INPUT' ? el : el.querySelector('input')
    if (!input || (input.type !== 'number' && input.type !== 'text')) return
    
    const currentDecimals = getDecimals(binding)
    const lastDecimals = el._precisionLastDecimals
    const isFocused = document.activeElement === input
    const hasValue = input.value !== ''
    const shouldFormatWhenIdle = hasValue && !isFocused // not actively typing
    const shouldFormatWhenDisabled = hasValue && input.disabled
    
    // Reformat when precision changes OR when not editing (e.g., initial render/read-only state)
    if ((currentDecimals !== lastDecimals && hasValue) || shouldFormatWhenIdle || shouldFormatWhenDisabled) {
      formatInput(input, currentDecimals, { grouping: shouldGroup(binding, input) })
    }
    el._precisionLastDecimals = currentDecimals
  },
  
  unbind(el) {
    // Handle both direct input elements and wrapper elements containing inputs
    const input = el.tagName === 'INPUT' ? el : el.querySelector('input')
    if (input) {
      if (el._precisionBlurHandler) input.removeEventListener('blur', el._precisionBlurHandler)
      if (el._precisionInputHandler) input.removeEventListener('input', el._precisionInputHandler)
    }
    delete el._precisionBlurHandler
    delete el._precisionInputHandler
    delete el._precisionLastDecimals
  }
}

